import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import NavbarV2 from '../components/navbar-v2';
import FooterV2 from '../components/footer-v2';
import { API_BASE_URL } from '../config';


interface WebCuz {
  _id: string;
  hatimId: string;
  hatimAdi: string;
  cuzNo: number;
  fullName: string;
  phone?: string;
  email?: string;
  status: 'pending' | 'completed';
  okumaTuru: string;
  source?: 'web' | 'mobile' | 'guest' | 'unknown';
  createdAt: string;
  completedAt?: string;
}

interface GroupSummary {
  hatimId: string;
  hatimAdi: string;
  okumaTuru: string;
  totalCuz: number;
  pendingCuz: number;
  completedCuz: number;
  completionRate: number;
  lastActivity: string;
}

interface Stats {
  totalGroups: number;
  totalCuz: number;
  pending: number;
  completed: number;
  web?: number;
  mobile?: number;
  guest?: number;
}

interface VideoItem {
  _id: string;
  title: string;
  description: string;
  category: string;
  mimeType: string;
  fileSize: number;
  fileSizeMB: string;
  viewCount: number;
  streamUrl: string;
  isActive: boolean;
  createdAt: string;
}

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [stats, setStats] = useState<Stats>({ totalGroups: 0, totalCuz: 0, pending: 0, completed: 0, web: 0, mobile: 0, guest: 0 });
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'activity' | 'progress'>('activity');
  const [activeTab, setActiveTab] = useState<'groups' | 'videos'>('groups');

  // Video states
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [videoTitle, setVideoTitle] = useState('');
  const [videoDesc, setVideoDesc] = useState('');
  const [videoCategory, setVideoCategory] = useState('egitim');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [videoMsg, setVideoMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const user = localStorage.getItem('adminUser');

    if (!token || !user) {
      navigate('/admin/login');
      return;
    }

    const userData = JSON.parse(user);
    if (userData.role !== 'admin' && userData.role !== 'editor') {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      navigate('/admin/login');
      return;
    }

    fetchGroups();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/groups/admin/web-cuzler`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
        return;
      }

      const data = await response.json();

      if (data.success) {
        // Backend'den gelen groups dizisini kullan (cüz alınmamış gruplar dahil)
        const groupList: GroupSummary[] = (data.groups || []).map((g: any) => {
          let groupName = g.name;
          try { groupName = decodeURIComponent(g.name); } catch {}
          
          const totalCuz = g.totalCuz || 0;
          const takenCuz = g.takenCuz || 0;
          const completedCuz = g.completedCuz || 0;
          const pendingCuz = takenCuz - completedCuz;
          
          return {
            hatimId: g._id,
            hatimAdi: groupName,
            okumaTuru: g.readingType || 'sabit',
            totalCuz: totalCuz,
            pendingCuz: pendingCuz > 0 ? pendingCuz : 0,
            completedCuz: completedCuz,
            completionRate: totalCuz > 0 ? Math.round((completedCuz / totalCuz) * 100) : 0,
            lastActivity: g.createdAt || ''
          };
        });
        
        // En son aktivite bilgisini cüz verisinden güncelle
        const activityMap = new Map<string, string>();
        data.data.forEach((cuz: WebCuz) => {
          const existing = activityMap.get(cuz.hatimId);
          if (!existing || new Date(cuz.createdAt) > new Date(existing)) {
            activityMap.set(cuz.hatimId, cuz.createdAt);
          }
        });
        groupList.forEach(g => {
          const latestActivity = activityMap.get(g.hatimId);
          if (latestActivity) g.lastActivity = latestActivity;
        });

        setGroups(groupList);

        // Genel istatistikler
        setStats({
          totalGroups: groupList.length,
          totalCuz: data.data.length,
          pending: data.stats.pending,
          completed: data.stats.completed,
          web: data.stats.web || 0,
          mobile: data.stats.mobile || 0,
          guest: data.stats.guest || 0
        });
      } else {
        setError(data.message || 'Veriler alınamadı');
      }
    } catch (err) {
      setError('Sunucu ile bağlantı kurulamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  // ===== VIDEO FUNCTIONS =====
  const fetchVideos = async () => {
    setVideosLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_BASE_URL}/api/videos/admin/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setVideos(data.videos || []);
    } catch (err) {
      console.error('Video listesi hatası:', err);
    } finally {
      setVideosLoading(false);
    }
  };

  const handleVideoUpload = async () => {
    if (!videoTitle.trim()) { setVideoMsg({ type: 'error', text: 'Video başlığı yazın' }); return; }
    if (!selectedFile) { setVideoMsg({ type: 'error', text: 'Video dosyası seçin' }); return; }

    setUploading(true);
    setUploadProgress(0);
    setVideoMsg(null);

    const formData = new FormData();
    formData.append('video', selectedFile);
    formData.append('title', videoTitle.trim());
    formData.append('description', videoDesc.trim());
    formData.append('category', videoCategory);

    const token = localStorage.getItem('adminToken');
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}/api/videos/admin/upload`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      setUploading(false);
      if (xhr.status === 201) {
        setVideoMsg({ type: 'success', text: 'Video başarıyla yüklendi!' });
        setVideoTitle(''); setVideoDesc(''); setVideoCategory('egitim');
        setSelectedFile(null); setUploadProgress(0);
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchVideos();
      } else {
        try {
          const data = JSON.parse(xhr.responseText);
          setVideoMsg({ type: 'error', text: data.message || 'Yükleme başarısız' });
        } catch { setVideoMsg({ type: 'error', text: 'Yükleme başarısız' }); }
      }
    };

    xhr.onerror = () => {
      setUploading(false);
      setVideoMsg({ type: 'error', text: 'Bağlantı hatası' });
    };

    xhr.send(formData);
  };

  const toggleVideoActive = async (id: string, isActive: boolean) => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_BASE_URL}/api/videos/admin/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ isActive })
      });
      fetchVideos();
    } catch (err) { console.error(err); }
  };

  const deleteVideo = async (id: string, title: string) => {
    if (!window.confirm(`"${title}" videosunu silmek istediğinize emin misiniz?`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_BASE_URL}/api/videos/admin/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchVideos();
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (activeTab === 'videos' && videos.length === 0) fetchVideos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} dk önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;
    
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'short'
    });
  };

  // Filtreleme ve sıralama
  const filteredGroups = groups
    .filter(g => {
      if (!searchTerm) return true;
      return g.hatimAdi.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.hatimAdi.localeCompare(b.hatimAdi);
      if (sortBy === 'activity') return new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime();
      if (sortBy === 'progress') return b.completionRate - a.completionRate;
      return 0;
    });

  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const adminDisplayName = (() => {
    const name = adminUser.name || adminUser.email || '';
    try { return decodeURIComponent(name); } catch { return name; }
  })();

  return (
    <>
      <NavbarV2 />
      <div className="bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-100 min-h-screen pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Admin Paneli
              </h1>
              <p className="text-gray-600">
                Tüm cüz alımlarını yönetin (Web + Mobil + Misafir)
              </p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center gap-4">
              <span className="text-sm text-gray-500">
                Hoş geldin, <span className="font-semibold">{adminDisplayName}</span>
              </span>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
              >
                Çıkış Yap
              </button>
            </div>
          </div>

          {/* Tab Buttons */}
          <div className="flex gap-2 mb-8 bg-white rounded-xl p-1.5 shadow-sm border border-gray-100 w-fit">
            <button
              onClick={() => setActiveTab('groups')}
              className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === 'groups'
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="mr-1.5">📋</span> Grup Yönetimi
            </button>
            <button
              onClick={() => setActiveTab('videos')}
              className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === 'videos'
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="mr-1.5">🎬</span> Video Yönetimi
            </button>
          </div>

          {/* ===== VIDEO TAB ===== */}
          {activeTab === 'videos' && (
            <div>
              {/* Upload Card */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Video Yükle
                </h2>

                {videoMsg && (
                  <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${
                    videoMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {videoMsg.text}
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Başlık *</label>
                    <input
                      type="text"
                      value={videoTitle}
                      onChange={(e) => setVideoTitle(e.target.value)}
                      placeholder="Ör: Uygulama Tanıtım Videosu"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                    <select
                      value={videoCategory}
                      onChange={(e) => setVideoCategory(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
                    >
                      <option value="egitim">Eğitim</option>
                      <option value="tanitim">Tanıtım</option>
                      <option value="diger">Diğer</option>
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                  <textarea
                    value={videoDesc}
                    onChange={(e) => setVideoDesc(e.target.value)}
                    rows={2}
                    placeholder="Video açıklaması (isteğe bağlı)"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none resize-none"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Video Dosyası</label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/50 transition-all"
                  >
                    {selectedFile ? (
                      <div>
                        <span className="text-2xl mb-1 block">✅</span>
                        <p className="text-sm font-semibold text-gray-800">{selectedFile.name}</p>
                        <p className="text-xs text-gray-500">{(selectedFile.size / (1024 * 1024)).toFixed(1)} MB</p>
                      </div>
                    ) : (
                      <div>
                        <span className="text-3xl mb-1 block">🎥</span>
                        <p className="text-sm font-medium text-gray-600">Tıklayarak video seçin</p>
                        <p className="text-xs text-gray-400">MP4, WebM, MOV • Maks. 200MB</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) setSelectedFile(f);
                    }}
                  />
                </div>

                {uploading && (
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 font-medium">Yükleniyor...</span>
                      <span className="font-semibold text-indigo-600">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div className="bg-indigo-600 h-2.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleVideoUpload}
                  disabled={uploading || !videoTitle.trim() || !selectedFile}
                  className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    uploading || !videoTitle.trim() || !selectedFile
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md'
                  }`}
                >
                  {uploading ? 'Yükleniyor...' : '📤 Yükle'}
                </button>
              </div>

              {/* Video List */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-800">Yüklenen Videolar</h2>
                  <button onClick={fetchVideos} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
                    🔄 Yenile
                  </button>
                </div>

                {videosLoading ? (
                  <div className="text-center py-10">
                    <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-3"></div>
                    <p className="text-gray-500 text-sm">Videolar yükleniyor...</p>
                  </div>
                ) : videos.length === 0 ? (
                  <div className="text-center py-10">
                    <span className="text-4xl mb-2 block">🎬</span>
                    <p className="text-gray-500">Henüz video yüklenmemiş</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {videos.map((v) => (
                      <div key={v._id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-800 truncate">{v.title}</h4>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {v.fileSizeMB} MB • {v.category} • {v.viewCount || 0} görüntüleme • {new Date(v.createdAt).toLocaleDateString('tr-TR')}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5 truncate">
                            {API_BASE_URL}/api/videos/stream/{v._id}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${v.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {v.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => window.open(`${API_BASE_URL}${v.streamUrl}`, '_blank')}
                            className="p-2 rounded-lg text-gray-500 hover:bg-indigo-100 hover:text-indigo-600 transition"
                            title="Önizle"
                          >
                            👁️
                          </button>
                          <button
                            onClick={() => toggleVideoActive(v._id, !v.isActive)}
                            className="p-2 rounded-lg text-gray-500 hover:bg-amber-100 hover:text-amber-600 transition"
                            title={v.isActive ? 'Pasife Al' : 'Aktife Al'}
                          >
                            {v.isActive ? '🔒' : '🔓'}
                          </button>
                          <button
                            onClick={() => deleteVideo(v._id, v.title)}
                            className="p-2 rounded-lg text-gray-500 hover:bg-red-100 hover:text-red-600 transition"
                            title="Sil"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===== GROUPS TAB ===== */}
          {activeTab === 'groups' && (<>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Toplam Grup</p>
                  <p className="text-3xl font-bold text-indigo-600">{stats.totalGroups}</p>
                </div>
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Toplam Cüz</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.totalCuz}</p>
                  {(stats.web || stats.mobile || stats.guest) ? (
                    <p className="text-xs text-gray-400 mt-1">
                      {stats.mobile ? `📱${stats.mobile}` : ''}
                      {stats.web ? ` 🌐${stats.web}` : ''}
                      {stats.guest ? ` 👤${stats.guest}` : ''}
                    </p>
                  ) : null}
                </div>
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Bekleyen</p>
                  <p className="text-3xl font-bold text-amber-600">{stats.pending}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Tamamlanan</p>
                  <p className="text-3xl font-bold text-emerald-600">{stats.completed}</p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Search & Sort */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            {/* Search */}
            <div className="flex-1 relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Grup adı ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all bg-white"
              />
            </div>
            {/* Sort */}
            <div className="flex gap-2">
              <span className="flex items-center text-sm text-gray-500 mr-2">Sırala:</span>
              {([
                { key: 'activity', label: 'Son Aktivite' },
                { key: 'name', label: 'İsim' },
                { key: 'progress', label: 'İlerleme' }
              ] as const).map((option) => (
                <button
                  key={option.key}
                  onClick={() => setSortBy(option.key)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    sortBy === option.key
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-red-600">
              {error}
            </div>
          )}

          {/* Loading */}
          {loading ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-500">Gruplar yükleniyor...</p>
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-gray-500">
                {searchTerm ? 'Arama sonucu bulunamadı' : 'Henüz web\'den cüz alınan grup yok'}
              </p>
            </div>
          ) : (
            /* Groups Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredGroups.map((group) => (
                <Link
                  key={group.hatimId}
                  to={`/admin/group/${group.hatimId}`}
                  className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-lg hover:border-indigo-200 transition-all group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-800 truncate group-hover:text-indigo-600 transition-colors">
                        {group.hatimAdi}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {group.okumaTuru === 'donerli' ? '🔄 Dönerli' : '📌 Sabit'} Hatim
                      </p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>

                  {/* Progress */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">İlerleme</span>
                      <span className="font-semibold text-indigo-600">{group.completionRate}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          group.completionRate === 100
                            ? 'bg-emerald-500'
                            : 'bg-gradient-to-r from-indigo-500 to-purple-500'
                        }`}
                        style={{ width: `${group.completionRate}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center text-gray-600">
                        <span className="w-2 h-2 rounded-full bg-amber-400 mr-1.5"></span>
                        {group.pendingCuz} bekleyen
                      </span>
                      <span className="flex items-center text-gray-600">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 mr-1.5"></span>
                        {group.completedCuz} tamam
                      </span>
                    </div>
                  </div>

                  {/* Last Activity */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-400">
                      Son aktivite: {formatDate(group.lastActivity)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
          </>)}
        </div>
      </div>
      <FooterV2 />
    </>
  );
};

export default AdminPanel;
